/**
 * Responsibility: subscribe to a document or a collection, and keep it current.
 *
 * `onSnapshot` rather than a fetch, for three reasons in order of how much they
 * matter.
 *
 * It works without the hub, which is the entire point of reading Firestore
 * directly. It works without the network at all, because the local cache
 * answers first and reconciles later. And it updates across devices for free:
 * a card added on a phone appears on the desktop without either of them asking
 * again, which is not something the hub's REST surface ever did.
 *
 * **`fromCache` is surfaced, not hidden.** A snapshot served from the local
 * copy is real data and should be shown - but the page needs to be able to say
 * so, because "this is what we last saw" and "this is what is true now" are
 * different claims and only one of them justifies acting on it.
 */

import { useEffect, useState } from 'react';
import {
  collection as firestoreCollection,
  doc as firestoreDoc,
  limit as firestoreLimit,
  onSnapshot,
  orderBy,
  query,
  type DocumentData,
  type QueryConstraint,
} from 'firebase/firestore';

import { db } from './client';

/** What a live read knows, including where the answer came from. */
export interface Live<T> {
  data: T | undefined;
  loading: boolean;
  /** Served from the on-device copy rather than the server. */
  stale: boolean;
  error: Error | undefined;
}

const IDLE: Live<never> = { data: undefined, loading: false, stale: false, error: undefined };

/** One document, kept current. `path` of null means "not yet known". */
export function useLiveDocument<T = DocumentData>(path: string | null): Live<T> {
  const [state, setState] = useState<Live<T>>({ ...IDLE, loading: path !== null });

  useEffect(() => {
    const firestore = db();
    if (!firestore || !path) {
      setState({ ...IDLE });
      return;
    }
    setState((previous) => ({ ...previous, loading: true }));

    return onSnapshot(
      firestoreDoc(firestore, path),
      (snapshot) =>
        setState({
          data: snapshot.exists() ? (snapshot.data() as T) : undefined,
          loading: false,
          stale: snapshot.metadata.fromCache,
          error: undefined,
        }),
      (error) => setState({ ...IDLE, error }),
    );
  }, [path]);

  return state;
}

/** One document and its id, as the rest of the app expects to see a row. */
export type WithId<T> = T & { id: string };

interface LiveCollectionOptions {
  /** Field to order by. Omitted means document id, which is always indexed. */
  orderByField?: string;
  descending?: boolean;
  /**
   * How many to read.
   *
   * **Not optional in spirit.** Firestore bills per document read and a
   * collection with no ceiling is a page that costs more every month. A caller
   * that genuinely wants everything should say so with a number.
   */
  max?: number;
}

/** A collection, kept current. `path` of null means "not yet known". */
export function useLiveCollection<T = DocumentData>(
  path: string | null,
  options: LiveCollectionOptions = {},
): Live<WithId<T>[]> {
  const { orderByField, descending = false, max = 500 } = options;
  const [state, setState] = useState<Live<WithId<T>[]>>({
    ...IDLE,
    loading: path !== null,
  });

  useEffect(() => {
    const firestore = db();
    if (!firestore || !path) {
      setState({ ...IDLE });
      return;
    }
    setState((previous) => ({ ...previous, loading: true }));

    const constraints: QueryConstraint[] = [];
    if (orderByField) constraints.push(orderBy(orderByField, descending ? 'desc' : 'asc'));
    constraints.push(firestoreLimit(max));

    return onSnapshot(
      query(firestoreCollection(firestore, path), ...constraints),
      (snapshot) =>
        setState({
          data: snapshot.docs.map((row) => ({ id: row.id, ...(row.data() as T) })),
          loading: false,
          stale: snapshot.metadata.fromCache,
          error: undefined,
        }),
      (error) => setState({ ...IDLE, error }),
    );
  }, [path, orderByField, descending, max]);

  return state;
}
